import { CardRow } from 'components/CardRow';
import { pureComponent } from 'lib/pureComponent';
import { theme } from 'lib/theme';
import React from 'react';
import {
  dateMonth,
  dateQuarter,
  dateWeek,
  dateYear,
  filterArrayEmpty,
  formatDate,
  formatDateRange,
  randomKey,
} from 'lib/riskbase';
import {
  API_DATE_FORMAT,
  UI_DATE_FORMAT_LONG_MONTH_YEAR,
  UI_DATE_FORMAT_LONG_WITHOUT_YEAR,
} from 'lib/constants';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, eventSelectScreenSelection } from 'store/actions';
import { ModalScreens } from 'components/Navigation/types';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { DateField } from './DateField';

interface Props {
  label?: string;
  placeholderFrom?: string;
  placeholderTo?: string;
  fromDate?: string;
  toDate?: string;
  onChange: (value1: string, value2: string) => void;
  fromValue: string;
  toValue: string;
}

export const DateRangeField: React.FC<Props> = pureComponent(
  ({ label, placeholderFrom, placeholderTo, onChange, fromValue, toValue }) => {
    const morePresetsSelectRef = React.useRef(randomKey());
    const dispatch = useDispatch();

    const getPresetOptions = React.useCallback(() => {
      const thisWeek = dateWeek(new Date());
      const thisMonth = dateMonth(new Date());
      const thisQuarter = dateQuarter(new Date());
      const thisYear = dateYear(new Date());

      const lastWeek = dateWeek(subWeeks(new Date(), 1));
      const lastMonth = dateMonth(subMonths(new Date(), 1));
      const lastQuarter = dateQuarter(subDays(thisQuarter.startDate, 1));
      const lastYear = dateYear(subYears(new Date(), 1));

      const nextWeek = dateWeek(addWeeks(new Date(), 1));
      const nextMonth = dateMonth(addMonths(new Date(), 1));
      const nextQuarter = dateQuarter(addDays(thisQuarter.endDate, 1));
      const nextYear = dateYear(addYears(new Date(), 1));

      return {
        thisWeek: {
          key: 'thisWeek',
          label: formatDateRange(thisWeek.startDate, thisWeek.endDate, 'long'),
          labelShort: formatDateRange(
            thisWeek.startDate,
            thisWeek.endDate,
            'short',
            false,
          ),
          startDate: thisWeek.startDate,
          endDate: thisWeek.endDate,
        },
        thisMonth: {
          key: 'thisMonth',
          label: formatDate(
            thisMonth.startDate,
            UI_DATE_FORMAT_LONG_MONTH_YEAR,
          ),
          labelShort: formatDate(thisMonth.startDate, 'MMMM'),
          startDate: thisMonth.startDate,
          endDate: thisMonth.endDate,
        },
        thisQuarter: {
          key: 'thisQuarter',
          label: thisQuarter.months.join(', '),
          labelShort: thisQuarter.monthsShort.join(' '),
          startDate: thisQuarter.startDate,
          endDate: thisQuarter.endDate,
        },
        thisYear: {
          key: 'thisYear',
          label: formatDateRange(thisYear.startDate, thisYear.endDate, 'long'),
          labelShort: formatDate(thisYear.startDate, 'yyyy'),
          startDate: thisYear.startDate,
          endDate: thisYear.endDate,
        },
        lastWeek: {
          key: 'lastWeek',
          label: formatDateRange(lastWeek.startDate, lastWeek.endDate, 'long'),
          startDate: lastWeek.startDate,
          endDate: lastWeek.endDate,
        },
        lastMonth: {
          key: 'lastMonth',
          label: formatDate(
            lastMonth.startDate,
            UI_DATE_FORMAT_LONG_MONTH_YEAR,
          ),
          startDate: lastMonth.startDate,
          endDate: lastMonth.endDate,
        },
        lastQuarter: {
          key: 'lastQuarter',
          label: lastQuarter.months.join(', '),
          startDate: lastQuarter.startDate,
          endDate: lastQuarter.endDate,
        },
        lastYear: {
          key: 'lastYear',
          label: formatDate(lastYear.startDate, 'yyyy'),
          startDate: lastYear.startDate,
          endDate: lastYear.endDate,
        },
        nextWeek: {
          key: 'nextWeek',
          label: formatDateRange(nextWeek.startDate, nextWeek.endDate, 'long'),
          startDate: nextWeek.startDate,
          endDate: nextWeek.endDate,
        },
        nextMonth: {
          key: 'nextMonth',
          label: formatDate(
            nextMonth.startDate,
            UI_DATE_FORMAT_LONG_MONTH_YEAR,
          ),
          startDate: nextMonth.startDate,
          endDate: nextMonth.endDate,
        },
        nextQuarter: {
          key: 'nextQuarter',
          label: nextQuarter.months.join(', '),
          startDate: nextQuarter.startDate,
          endDate: nextQuarter.endDate,
        },
        nextYear: {
          key: 'nextYear',
          label: formatDate(nextYear.startDate, 'yyyy'),
          startDate: nextYear.startDate,
          endDate: nextYear.endDate,
        },
      };
    }, []);

    const handlePressShowMorePresets = () => {
      const presetOptions = getPresetOptions();
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            title: label || 'Other Date Ranges',
            eventKey: morePresetsSelectRef.current,
            options: [
              {
                name: 'Last Week',
                value: presetOptions.lastWeek.label,
                internalValue: presetOptions.lastWeek.key,
              },
              {
                name: 'Last Month',
                value: presetOptions.lastMonth.label,
                internalValue: presetOptions.lastMonth.key,
              },
              {
                name: 'Last Quarter',
                value: presetOptions.lastQuarter.label,
                internalValue: presetOptions.lastQuarter.key,
              },
              {
                name: 'Last Year',
                value: presetOptions.lastYear.label,
                internalValue: presetOptions.lastYear.key,
              },
              '-',
              {
                name: 'Next Week',
                value: presetOptions.nextWeek.label,
                internalValue: presetOptions.nextWeek.key,
              },
              {
                name: 'Next Month',
                value: presetOptions.nextMonth.label,
                internalValue: presetOptions.nextMonth.key,
              },
              {
                name: 'Next Quarter',
                value: presetOptions.nextQuarter.label,
                internalValue: presetOptions.nextQuarter.key,
              },
              {
                name: 'Next Year',
                value: presetOptions.nextYear.label,
                internalValue: presetOptions.nextYear.key,
              },
            ],
          },
          size: 'small',
        }),
      );
    };

    const handleChange = React.useCallback(
      (newFromValue: string, newToValue: string) =>
        newFromValue !== fromValue || newToValue !== toValue
          ? onChange(newFromValue, newToValue)
          : null,
      [fromValue, toValue, onChange],
    );

    React.useEffect(() => {
      const eventId = doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === morePresetsSelectRef.current) {
          const value = event.payload.stringValues[0] as
            | undefined
            | keyof ReturnType<typeof getPresetOptions>;
          const preset = getPresetOptions()[value || 'lastMonth'];
          if (preset) {
            handleChange(
              formatDate(preset.startDate, API_DATE_FORMAT),
              formatDate(preset.endDate, API_DATE_FORMAT),
            );
          }
        }
      });
      return () => {
        doRemoveEvent(eventId);
      };
    }, [getPresetOptions, handleChange]);

    return (
      <CardRow
        label={label}
        labelAlignTop={!!label}
        style={{ backgroundColor: theme.colors.white }}
        contentStyle={[
          { paddingBottom: 0 },
          !label ? { paddingTop: 0 } : undefined,
        ]}
        childrenStyle={{
          flexDirection: 'row',
          columnGap: theme.paddingHorizontal,
        }}
        menuOptions={[
          {
            text: 'Today',
            horizontalText: true,
            subText: formatDate(new Date(), UI_DATE_FORMAT_LONG_WITHOUT_YEAR),
            onPress: () =>
              handleChange(
                formatDate(new Date(), API_DATE_FORMAT),
                formatDate(new Date(), API_DATE_FORMAT),
              ),
          },
          {
            text: 'Week',
            subText: getPresetOptions().thisWeek.labelShort,
            horizontalText: true,
            onPress: () =>
              handleChange(
                formatDate(
                  getPresetOptions().thisWeek.startDate,
                  API_DATE_FORMAT,
                ),
                formatDate(
                  getPresetOptions().thisWeek.endDate,
                  API_DATE_FORMAT,
                ),
              ),
          },
          {
            text: 'Month',
            subText: getPresetOptions().thisMonth.labelShort,
            horizontalText: true,
            onPress: () =>
              onChange(
                formatDate(
                  getPresetOptions().thisMonth.startDate,
                  API_DATE_FORMAT,
                ),
                formatDate(
                  getPresetOptions().thisMonth.endDate,
                  API_DATE_FORMAT,
                ),
              ),
          },
          {
            text: 'Quarter',
            subText: getPresetOptions().thisQuarter.labelShort,
            horizontalText: true,
            onPress: () =>
              handleChange(
                formatDate(
                  getPresetOptions().thisQuarter.startDate,
                  API_DATE_FORMAT,
                ),
                formatDate(
                  getPresetOptions().thisQuarter.endDate,
                  API_DATE_FORMAT,
                ),
              ),
          },
          {
            text: 'Year',
            subText: getPresetOptions().thisYear.labelShort,
            horizontalText: true,
            onPress: () =>
              handleChange(
                formatDate(
                  getPresetOptions().thisYear.startDate,
                  API_DATE_FORMAT,
                ),
                formatDate(
                  getPresetOptions().thisYear.endDate,
                  API_DATE_FORMAT,
                ),
              ),
          },
          {
            text: 'More options...',
            onPress: handlePressShowMorePresets,
          },
        ].filter(filterArrayEmpty)}
      >
        <DateField
          value={fromValue}
          max={toValue ? new Date(toValue) : undefined}
          onChange={(newValue) => handleChange(newValue, toValue)}
          placeholder={placeholderFrom}
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          shrinkToWidth
          showClearButton
          showFieldTypeIcon={false}
        />
        <DateField
          value={toValue}
          min={fromValue ? new Date(fromValue) : undefined}
          onChange={(newValue) => handleChange(fromValue, newValue)}
          placeholder={placeholderTo}
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          shrinkToWidth
          showClearButton
          showFieldTypeIcon={false}
        />
      </CardRow>
    );
  },
);
